// Utility functions for formatting data in the admin dashboard

import type { ChangeMetric, FormattedChange } from '../types';

// Format numbers with appropriate suffixes
export const formatNumber = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return '0';

  const number = Number(num);
  if (isNaN(number)) return '0';

  if (number >= 1000000) {
    return (number / 1000000).toFixed(1) + 'M';
  } else if (number >= 1000) {
    return (number / 1000).toFixed(1) + 'K';
  } else {
    return number.toLocaleString();
  }
};

// Format percentage values
export const formatPercentage = (value: number | null | undefined, decimals: number = 1): string => {
  if (value === null || value === undefined) return '0%';
  const number = Number(value);
  if (isNaN(number)) return '0%';
  return `${number.toFixed(decimals)}%`;
};

// Format currency (Bulgarian Lev)
export const formatCurrency = (amount: number | null | undefined, currency: string = 'BGN'): string => {
  if (amount === null || amount === undefined) return '0 лв.';

  const number = Number(amount);
  if (isNaN(number)) return '0 лв.';

  if (currency === 'BGN') {
    return `${number.toFixed(2)} лв.`;
  } else {
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: currency
    }).format(number);
  }
};

// Format dates for Bulgarian locale
export const formatDate = (date: string | Date | null | undefined, options: Intl.DateTimeFormatOptions = {}): string => {
  if (!date) return '';

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };

  return dateObj.toLocaleDateString('bg-BG', defaultOptions);
};

// Format time for Bulgarian locale
export const formatTime = (date: string | Date | null | undefined): string => {
  if (!date) return '';

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';

  return dateObj.toLocaleTimeString('bg-BG', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Format datetime for Bulgarian locale
export const formatDateTime = (date: string | Date | null | undefined): string => {
  if (!date) return '';

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';

  return dateObj.toLocaleString('bg-BG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Format relative time (e.g., "2 hours ago")
export const formatRelativeTime = (date: string | Date | null | undefined): string => {
  if (!date) return '';

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) {
    return 'Сега';
  } else if (diffMins < 60) {
    return `Преди ${diffMins} мин.`;
  } else if (diffHours < 24) {
    return `Преди ${diffHours} ч.`;
  } else if (diffDays < 7) {
    return `Преди ${diffDays} дни`;
  } else {
    return formatDate(date);
  }
};

// Format duration in minutes/hours
export const formatDuration = (minutes: number | null | undefined): string => {
  if (minutes === null || minutes === undefined) return '0 мин.';

  const mins = Number(minutes);
  if (isNaN(mins)) return '0 мин.';

  if (mins < 60) {
    return `${Math.round(mins)} мин.`;
  } else {
    const hours = Math.floor(mins / 60);
    const remainingMins = Math.round(mins % 60);
    if (remainingMins === 0) {
      return `${hours} ч.`;
    } else {
      return `${hours} ч. ${remainingMins} мин.`;
    }
  }
};

// Format file sizes
export const formatFileSize = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined) return '0 B';

  const number = Number(bytes);
  if (isNaN(number)) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = number;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

// Format change indicators
export const formatChange = (metric: ChangeMetric | null | undefined): FormattedChange | null => {
  if (!metric || metric.change === null || metric.change === undefined) {
    return null;
  }

  const change = Number(metric.change);
  if (isNaN(change)) return null;

  const absChange = Math.abs(change);
  const sign = change >= 0 ? '+' : '-';
  const arrow = change >= 0 ? '↗' : '↘';
  const colorClass = change >= 0 ? 'positive' : 'negative';

  return {
    text: `${sign}${absChange.toFixed(1)}%`,
    arrow,
    colorClass,
    isPositive: change >= 0,
    isNegative: change < 0
  };
};

// Format Bulgarian cities (ensure proper capitalization)
export const formatCityName = (city: string | null | undefined): string => {
  if (!city) return '';

  // Common Bulgarian city name mappings
  const cityMappings: Record<string, string> = {
    'софия': 'София',
    'пловдив': 'Пловдив',
    'варна': 'Варна',
    'бургас': 'Бургас',
    'русе': 'Русе',
    'стара загора': 'Стара Загора',
    'плевен': 'Плевен',
    'сливен': 'Сливен',
    'добрич': 'Добрич',
    'шумен': 'Шумен'
  };

  const lowerCity = city.toLowerCase();
  return cityMappings[lowerCity] || city;
};

// Format route strings
export const formatRoute = (origin: string | null | undefined, destination: string | null | undefined): string => {
  const formattedOrigin = formatCityName(origin);
  const formattedDestination = formatCityName(destination);
  return `${formattedOrigin} → ${formattedDestination}`;
};

// Generate chart colors based on index
export const getChartColor = (index: number, opacity: number = 1): string => {
  const colors = [
    `rgba(59, 130, 246, ${opacity})`,   // Blue
    `rgba(16, 185, 129, ${opacity})`,   // Green
    `rgba(245, 158, 11, ${opacity})`,   // Yellow
    `rgba(239, 68, 68, ${opacity})`,    // Red
    `rgba(139, 92, 246, ${opacity})`,   // Purple
    `rgba(236, 72, 153, ${opacity})`,   // Pink
    `rgba(6, 182, 212, ${opacity})`,    // Cyan
    `rgba(99, 102, 241, ${opacity})`,   // Indigo
  ];

  return colors[index % colors.length];
};

// Validate and format form inputs
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validateDateRange = (startDate: string | null | undefined, endDate: string | null | undefined): boolean => {
  if (!startDate || !endDate) return true; // Allow empty dates

  const start = new Date(startDate);
  const end = new Date(endDate);

  return start <= end && start <= new Date();
};

// Export all formatters as default
const formatters = {
  formatNumber,
  formatPercentage,
  formatCurrency,
  formatDate,
  formatTime,
  formatDateTime,
  formatRelativeTime,
  formatDuration,
  formatFileSize,
  formatChange,
  formatCityName,
  formatRoute,
  getChartColor,
  validateEmail,
  validateDateRange
};

export default formatters;
